// [목적] 좌측 글로벌 네비 — 로고 / 검색 / 메인 NAV / 컬렉션 / 시스템 / 사용자 + 알림 unread 뱃지
// [이유] docs/플리오랩/web/shell.jsx 사이드바 디자인 포팅 (Notion·Linear 톤).
//         W3-1(design-alignment): collapsed 토글 추가 — 접힘(60px, 아이콘만)/펼침(232px) 전환.
//         ADR-DA3: "시스템(알림/설정)" 섹션은 의도된 확장으로 유지(시안에 없으나 제거 금지).
//         fe-be-gap-tier1 T11(ADR-T1-3·ADR-TR-5): COLLECTIONS mock 제거 →
//           collection.client.list({ kind:'official', limit:4 }) official 최신순 4건.
//           항목 클릭 → /collections/:id(ADR-TR-7). 0건·로딩 graceful.
//         fe-be-gap-tier3 T10(ADR-T3-6): 알림 NAV 항목에 unread 뱃지 추가.
//           useUnreadCount — 진입 시 + window focus refetch, 주기 폴링·실시간 push 없음.
//           collapsed 상태에서도 뱃지 표시(아이콘 위 작은 dot).
//         SID-FE-01(ADR D-SID-SIDEBAR-LOADING): ME mock 제거 → useAuth 실데이터.
//           status='loading' → 스켈레톤. status='guest'/user=null → 인증 UI 미표시.
//           status='authenticated' → 실데이터. mock/guest 폴백 렌더 절대 금지(flash of wrong state 방지).
//         LGO-FE-01(auth-logout-hardening Wave 3, design-check B안 확정):
//           하단 사용자 영역을 클릭 가능한 트리거로 전환 → MUI Menu 소형 팝오버.
//           메뉴 항목: 설정(→/settings) + 로그아웃(→useAuth().logout()). collapsed 시 아바타 클릭도 동일.
//           접근성: aria-haspopup="menu", aria-expanded, aria-label="사용자 메뉴", role="menuitem", Esc 닫기.
// [사이드 이팩트] App.tsx 에서 collapsed/onToggle prop 주입. 데스크톱 전용 — 모바일은 collapsed 무관.
//                 접힘 시 검색바·텍스트 라벨·컬렉션 섹션 숨김(collapsed=true → 컬렉션 섹션 숨김 동작 보존).
//                 ME mock import 제거로 design-mock.ts 의 MeMock 타입/ME 상수는 Sidebar 에서 미참조.
//                 MUI Menu import 추가 — 팝오버 메뉴 렌더(LGO-FE-01).
//                 useState, useCallback import 추가 — 팝오버 앵커 상태 관리.
// [수정 시 고려사항] 라우트 경로 변동 시 NAV 배열의 to/match 동시 수정.
//                   collapsed 상태는 App.tsx 에서 관리 — 본 컴포넌트는 prop 수신만.
//                   공식 컬렉션 시드 없으면 0건 graceful(섹션 라벨만 표시).
//                   useAuth().user.primaryProvider 추가 — provider 표시 필요 시 providerLabel 헬퍼 참조.
//                   모바일에서는 팝오버 트리거 미노출 — 현행 ProfileMobile ⚙→설정→로그아웃 유지(시안 정본).
//                   [수정: 디자인 QA 2026-09-20] NAV/시스템 항목 이모지 글리프(⌕⌘⊕🎈❑◐⌕🔔⚙) →
//                   LineIcons.tsx 공용 라인 아이콘으로 교체. 아이콘 키는 기존 n.id/s.id 를 그대로 재사용
//                   (NAV 배열/라우트 구조 변경 없음) — 색상은 항상 currentColor 상속으로 기존 토큰을 따른다.
//                   [G1-4, 2026-09-17] 검색(⌘K)→CommandPalette.tsx(GET /folios?q= 재사용), 새 컬렉션→
//                   NewCollectionModal.tsx(POST /collections), ⌘N→navigate('/chat') 전역 keydown 배선 완료.

import { Box, ButtonBase, Menu, MenuItem, Skeleton, Stack, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import * as collectionClient from '../../api/collection.client.js';
import { queryKeys } from '../../api/queryKeys.js';
import { useUnreadCount } from '../../hooks/useNotifications.js';
import { useAuth } from '../../hooks/useAuth.js';
import { CommandPalette } from './CommandPalette.js';
import { NewCollectionModal } from './NewCollectionModal.js';
import { LoginPromptModal } from '../auth/LoginPromptModal.js';
import { LineIcon, type LineIconName } from '../icons/LineIcons.js';

// [수정: 2026-09-20 단축키 배지 제거] 사이드바 검색/새 폴리오 버튼 옆 ⌘K·⌘N 표기 UI 제거 요청 —
// Cmd/Ctrl+K, Cmd/Ctrl+N 실제 키보드 동작(아래 keydown 핸들러)은 그대로 유지하고,
// 화면에 노출되던 단축키 뱃지 텍스트만 삭제한다(SHORTCUT_SEARCH/SHORTCUT_NEW/shortcut 필드 제거).
const NAV: ReadonlyArray<{
  id: string;
  label: string;
  icon: string;
  to: string;
}> = [
  { id: 'home', label: '홈', icon: '⌂', to: '/' },
  { id: 'chat', label: '새 폴리오', icon: '✦', to: '/chat' },
  { id: 'explore', label: '발견', icon: '⊕', to: '/explore' },
  // [수정: 디자인 개편] 강원 축제 전용 페이지 네비 항목 추가
  { id: 'festivals', label: '축제', icon: '🎈', to: '/festivals' },
  { id: 'folio', label: '내 폴리오', icon: '❑', to: '/me/folios' },
  { id: 'profile', label: '프로필', icon: '◐', to: '/profile' },
];

const c = tokens.color;
const L = 'light' as const;
const SIDE_BG = c.paper[1][L];
const BORDER = c.line.base[L];
const BORDER_SOFT = c.line.soft[L];
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BRAND = c.brand[500][L];
// [2026-09-18] 활성 네비 라벨 전용 진한 브랜드 — brand[500] 텍스트가 활성 배경(rgba(31,111,235,.08))
// 위에서 3.97:1 로 AA 미달이었다(docs/qa/2026-09-17_contrast-measure.md "홈"/"발견"). brand[600] 은
// 동일 배경 위 5.73:1(근사) — 아이콘/보더 등 넓은 면적은 기존 BRAND(brand[500]) 유지.
const BRAND_TEXT_STRONG = c.brand[600][L];
const PAPER2 = c.paper[2][L];
const PAPER0 = c.paper[0][L];

interface SidebarProps {
  width: number;
  /** 데스크톱 사이드바 접힘 여부 (true=60px 아이콘만, false=232px 풀) */
  collapsed: boolean;
  /** collapse 토글 콜백 (데스크톱 전용) */
  onToggle: () => void;
  mobile: boolean;
  mobileOpen: boolean;
  onClose: () => void;
}

/**
 * [목적] 좌측 글로벌 사이드바 — 데스크톱 collapse 토글 + 모바일 슬라이드 오버레이
 * [이유] W3-1: 시안 shell.jsx 의 collapsed/onToggle 패턴 포팅. 접힘 시 60px(아이콘만).
 * [사이드 이팩트] 접힘 시 로고 텍스트·검색·컬렉션 섹션·사용자 이름 등 텍스트 요소 숨김.
 * [수정 시 고려사항] 시스템 섹션(ADR-DA3)은 접힘/펼침 모두 렌더 유지.
 */
export function Sidebar({
  width,
  collapsed,
  onToggle,
  mobile,
  mobileOpen,
  onClose,
}: SidebarProps): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // [목적] 실인증 사용자 정보 — SID-FE-01(ADR D-SID-SIDEBAR-LOADING)
  // [이유] ME mock 제거. status 분기로 로딩=스켈레톤, guest=미표시, authenticated=실데이터.
  //         mock/fallback 렌더 금지 — flash of wrong state 방지.
  // [사이드 이팩트] AuthProvider 하위에서만 사용 가능(App.tsx 구조상 보장됨).
  // [수정 시 고려사항] user.primaryProvider 추가됨(SID-BE-01) — provider 표시 필요 시 활용.
  const { status: authStatus, user: authUser, logout } = useAuth();

  // ── LGO-FE-01: 사용자 메뉴 팝오버 상태 ────────────────────────────────────
  // [목적] 하단 사용자 영역 클릭 시 MUI Menu 팝오버 앵커 관리 (B안 — design-check 확정)
  // [이유] design-check 보고서: 데스크톱 Sidebar 사용자 영역을 인터랙티브 트리거로 전환.
  //         모바일(mobile=true) 에서는 팝오버 미사용(ProfileMobile 경로 유지) — menuAnchorEl 조건 분기.
  // [사이드 이팩트] menuAnchorEl 이 null 이면 Menu 미렌더 → 성능 영향 없음.
  // [수정 시 고려사항] collapsed/expanded 양쪽에서 동일 팝오버 동작 — 앵커는 currentTarget(트리거 요소).
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const userMenuTriggerId = 'sidebar-user-menu-trigger';
  const userMenuId = 'sidebar-user-menu';

  // 팝오버 열기 — 데스크톱 전용 (모바일은 ProfileMobile 경로 유지)
  const handleUserAreaClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (mobile) return;
      setMenuAnchorEl(event.currentTarget);
    },
    [mobile],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleMenuSettings = useCallback(() => {
    handleMenuClose();
    navigate('/settings');
  }, [handleMenuClose, navigate]);

  const handleMenuLogout = useCallback(() => {
    handleMenuClose();
    void logout();
  }, [handleMenuClose, logout]);

  // [목적] 알림 미읽 카운트 — T10(ADR-T3-6) 진입 시 + window focus refetch
  // [이유] Sidebar 알림 NAV 아이콘에 unread 뱃지 표시. 주기 폴링·실시간 push 없음(ADR-T3-6).
  //         throwOnError: false 로 Sidebar 뱃지 실패 시 숨김 처리(그레이스풀).
  // [사이드 이팩트] collapsed 상태에서도 도트 뱃지 표시 — 아이콘 위 작은 원형.
  // [수정 시 고려사항] useUnreadCount 내부에서 refetchOnWindowFocus: true 설정됨.
  const notifUnreadCount = useUnreadCount();

  // ── G1-4: 커맨드 팔레트(⌘K) + 새 폴리오(⌘N) 전역 단축키 + 새 컬렉션 모달 상태 ──────
  // [목적] Sidebar 검색 버튼(⌘K 표기)·"새 컬렉션" 버튼·NAV ⌘N 표기가 실제 동작하도록 배선
  // [이유] 기존엔 표기만 있고 클릭/키보드 핸들러가 없던 죽은 UI(G1-4 위임 카드).
  // [사이드 이팩트] document 전역 keydown 리스너 등록 — Sidebar 는 데스크톱 전용 마운트(App.tsx)이므로
  //                모바일(BottomTabBar 경로)에서는 이 리스너가 아예 마운트되지 않는다(중복 우려 없음).
  // [수정 시 고려사항] 텍스트 입력 중(예: 검색창 자체)에는 ⌘K/⌘N 을 다시 가로채지 않도록 팔레트가
  //                   열려 있는 동안은 keydown 핸들러를 등록하지 않는다(early return).
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);

  // [목적] 게스트가 "새 컬렉션"을 눌렀을 때 실패 토스트 대신 로그인 유도 모달을 띄운다
  // [이유] QA P2-01: 컬렉션 생성은 인증 필수(BE)인데 게스트도 모달을 열고 제출한 뒤에야
  //        "컬렉션 생성에 실패했습니다"만 보였다 — 사용자는 로그인이 필요하다는 걸 알 수 없었다.
  // [사이드 이팩트] guest 는 NewCollectionModal 을 열지 않는다. status='loading'(세션 확인 중)에는 아무것도
  //                하지 않는다 — 이미 로그인한 사용자에게 로그인 유도가 잠깐 뜨는 오동작을 피하기 위함.
  // [수정 시 고려사항] useAuthGate 를 쓰지 않고 이미 이 컴포넌트가 읽는 authStatus 를 재사용한다
  //                   (Sidebar 테스트가 useAuth 만 모킹하고 AuthProvider 컨텍스트는 제공하지 않는다).
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const handleNewCollectionClick = useCallback((): void => {
    if (authStatus === 'authenticated') setNewCollectionOpen(true);
    else if (authStatus === 'guest') setLoginPromptOpen(true);
  }, [authStatus]);

  useEffect(() => {
    if (mobile) return;
    function handleKeyDown(e: KeyboardEvent): void {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (e.key.toLowerCase() === 'n') {
        e.preventDefault();
        navigate('/chat');
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobile, navigate]);

  // [목적] official 컬렉션 최신순 4건 — T11 ADR-T1-3·ADR-TR-5
  // [이유] COLLECTIONS mock 제거. kind='official', limit=4, created_at DESC(서버 기본순).
  //         0건·에러 graceful — 컬렉션 라벨만 표시하고 항목 없음.
  // [사이드 이팩트] collapsed=true 시 컬렉션 섹션 자체가 숨겨지므로 이 쿼리 결과 미표시(회귀 없음).
  // [수정 시 고려사항] staleTime 5분 — official 컬렉션은 자주 바뀌지 않음.
  const { data: collectionData } = useQuery({
    queryKey: queryKeys.collection.list({ kind: 'official', limit: 4 }),
    queryFn: () => collectionClient.list({ kind: 'official', limit: 4 }),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });
  const sidebarCollections = collectionData?.items ?? [];
  // [이유] startsWith 매칭은 '/profile' 과 '/me/folios' 처럼 prefix 가 겹치지 않는 경우에도
  //        예전에 두 항목이 같은 to('/profile')일 때 동시에 active 로 표시되던 버그를 막기 위해
  //        정확 매칭(=) 으로 통일. 후속 자식 라우트(예: /explore/cafe) 도입 시 별도 매칭 규칙 추가.
  const isActive = (to: string): boolean => pathname === to;

  const handleNav = (to: string): void => {
    navigate(to);
    if (mobile) onClose();
  };

  // 데스크톱에서 expanded 여부 (모바일은 항상 expanded)
  const expanded = mobile ? true : !collapsed;

  const overlaySx = mobile
    ? {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 50,
        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 220ms cubic-bezier(.2,0,0,1)',
        boxShadow: mobileOpen ? '0 0 40px rgba(0,0,0,.15)' : 'none',
      }
    : {
        position: 'sticky' as const,
        top: 0,
        height: '100vh',
        flexShrink: 0,
        // [이유] W3-1: 폭 전환 시 시안 shell.jsx transition 구현
        transition: 'width 180ms cubic-bezier(.2,0,0,1)',
        // [이유] TopBar(zIndex:10) 가 경계 걸침 토글 버튼(right:-11)의 돌출부를 덮는 버그 수정 —
        //         aside 는 스태킹 컨텍스트가 없어 버튼 zIndex:2 가 루트에서 TopBar 10 에 밀림.
        //         사이드바 레이어 전체를 TopBar 위(11)로 올려 해결. (시안 shell.jsx 도 동일 잠재 버그)
        // [수정 시 고려사항] TopBar zIndex 를 11 이상으로 올리면 재발. MUI Menu/Popover 는 portal(z 1300)이라 무관.
        zIndex: 11,
      };

  return (
    <>
      {mobile && mobileOpen && (
        <Box
          onClick={onClose}
          sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,.3)', zIndex: 49 }}
        />
      )}
      <Box
        component="aside"
        sx={{
          width,
          bgcolor: SIDE_BG,
          borderRight: `1px solid ${BORDER}`,
          display: 'flex',
          flexDirection: 'column',
          // position 은 overlaySx 에서 주입 (mobile=fixed, desktop=sticky)
          // [이유] aside 에 overflow 미지정(시안 shell.jsx 동일) — 내부 스크롤은 nav 의 overflowY:auto 담당.
          ...overlaySx,
        }}
      >
        {/* 로고 */}
        <Box
          sx={{
            p: expanded ? '18px' : '18px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            borderBottom: `1px solid ${BORDER_SOFT}`,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              // [이유] 로고 배지 그라디언트 — 시안 shell.jsx 원본값 유지(토큰에 brand 단계 없음).
              //         check-no-cool-hex FORBIDDEN_HEX 대상 아님.
              background: 'linear-gradient(135deg,#1F6FEB,#4A8AF0)',
              color: PAPER0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: '-0.5px',
              flexShrink: 0,
            }}
          >
            강
          </Box>
          {expanded && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component={RouterLink}
                to="/"
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: INK_HI,
                  letterSpacing: '-0.4px',
                  textDecoration: 'none',
                  display: 'block',
                }}
              >
                강원캔버스
              </Typography>
              <Typography
                sx={{
                  fontSize: 9.5,
                  color: INK_LOW,
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  mt: '1px',
                }}
              >
                GangwonCanvas
              </Typography>
            </Box>
          )}
        </Box>

        {/* 검색 (placeholder) — 접힘 시 숨김 */}
        {expanded && (
          <Box sx={{ p: '12px 12px 4px' }}>
            <ButtonBase
              onClick={() => setPaletteOpen(true)}
              aria-label="폴리오 검색"
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                bgcolor: PAPER2,
                borderRadius: '8px',
                p: '8px 11px',
                justifyContent: 'flex-start',
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              <Box component="span" sx={{ color: INK_LOW, display: 'flex' }}>
                <LineIcon name="search" size={14} />
              </Box>
              <Box
                component="span"
                sx={{ flex: 1, fontSize: 11.5, color: INK_LOW, textAlign: 'left' }}
              >
                검색
              </Box>
            </ButtonBase>
          </Box>
        )}

        {/* 메인 NAV */}
        <Box
          component="nav"
          aria-label="주 네비게이션"
          sx={{ flex: 1, p: '8px', overflowY: 'auto' }}
        >
          {NAV.map((n) => {
            const active = isActive(n.to);
            return (
              <ButtonBase
                key={n.id}
                onClick={() => handleNav(n.to)}
                aria-label={n.label}
                aria-current={active ? 'page' : undefined}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  bgcolor: active ? 'rgba(31,111,235,.08)' : 'transparent',
                  color: active ? BRAND_TEXT_STRONG : INK_HI,
                  borderRadius: '7px',
                  p: collapsed && !mobile ? '9px 0' : '8px 10px',
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                  mb: '1px',
                  justifyContent: collapsed && !mobile ? 'center' : 'flex-start',
                  '&:hover': { bgcolor: active ? 'rgba(31,111,235,.10)' : 'rgba(0,0,0,.03)' },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                <Box
                  component="span"
                  sx={{ width: 18, display: 'flex', justifyContent: 'center', flexShrink: 0 }}
                >
                  <LineIcon name={n.id as LineIconName} size={17} />
                </Box>
                {expanded && (
                  <Box
                    component="span"
                    sx={{ flex: 1, textAlign: 'left', letterSpacing: '-0.2px' }}
                  >
                    {n.label}
                  </Box>
                )}
              </ButtonBase>
            );
          })}

          {/* 컬렉션 섹션 — 접힘 시 숨김 */}
          {expanded && (
            <>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: INK_LOW,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  p: '18px 10px 6px',
                }}
              >
                컬렉션
              </Typography>
              {/* [목적] official 컬렉션 최신순 4건 렌더 — T11(ADR-T1-3) */}
              {/* [이유] COLLECTIONS mock 제거 → 실데이터. 0건이면 항목 없이 라벨만 표시(graceful). */}
              {sidebarCollections.map((col) => (
                <ButtonBase
                  key={col.id}
                  onClick={() => handleNav(`/collections/${col.id}`)}
                  aria-label={`컬렉션 ${col.title}`}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: INK_MID,
                    borderRadius: '7px',
                    p: '7px 10px',
                    fontSize: 12,
                    fontWeight: 500,
                    mb: '1px',
                    justifyContent: 'flex-start',
                    '&:hover': { bgcolor: 'rgba(0,0,0,.03)' },
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                  }}
                >
                  <Box component="span" sx={{ fontSize: 13, width: 18, textAlign: 'center' }}>
                    🏛
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      textAlign: 'left',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.title}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 9.5,
                      color: INK_LOW,
                      fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    }}
                  >
                    {col.itemCount}
                  </Box>
                </ButtonBase>
              ))}
              <ButtonBase
                onClick={handleNewCollectionClick}
                aria-label="새 컬렉션 만들기"
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: INK_LOW,
                  borderRadius: '7px',
                  p: '7px 10px',
                  fontSize: 11.5,
                  fontWeight: 500,
                  justifyContent: 'flex-start',
                  '&:hover': { bgcolor: 'rgba(0,0,0,.03)' },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                <Box component="span" sx={{ fontSize: 13, width: 18, textAlign: 'center' }}>
                  +
                </Box>
                <Box component="span">새 컬렉션</Box>
              </ButtonBase>
            </>
          )}

          {/* 시스템 진입 — ADR-DA3: 의도된 확장, 접힘/펼침 모두 유지 */}
          {expanded && (
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: INK_LOW,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                p: '18px 10px 6px',
              }}
            >
              시스템
            </Typography>
          )}
          {[
            { id: 'notifications', label: '알림', icon: '🔔', to: '/notifications' },
            { id: 'settings', label: '설정', icon: '⚙', to: '/settings' },
          ].map((s) => {
            const isNotif = s.id === 'notifications';
            const showBadge = isNotif && notifUnreadCount > 0;
            return (
              <ButtonBase
                key={s.id}
                onClick={() => handleNav(s.to)}
                aria-label={isNotif && showBadge ? `알림 (미읽 ${notifUnreadCount}개)` : s.label}
                aria-current={isActive(s.to) ? 'page' : undefined}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: INK_MID,
                  borderRadius: '7px',
                  p: collapsed && !mobile ? '9px 0' : '7px 10px',
                  fontSize: 12,
                  fontWeight: 500,
                  mb: '1px',
                  justifyContent: collapsed && !mobile ? 'center' : 'flex-start',
                  bgcolor: isActive(s.to) ? 'rgba(31,111,235,.06)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(0,0,0,.03)' },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                {/* 아이콘 + collapsed 뱃지 도트 */}
                <Box
                  component="span"
                  sx={{
                    width: 18,
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <LineIcon name={s.id as LineIconName} size={15} />
                  {/* collapsed 시 작은 dot 뱃지 — 펼침 시에는 우측 숫자 뱃지로 표시 */}
                  {showBadge && !expanded && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: BRAND,
                        border: `1.5px solid ${SIDE_BG}`,
                      }}
                    />
                  )}
                </Box>
                {expanded && (
                  <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
                    {s.label}
                  </Box>
                )}
                {/* 펼침 시 숫자 뱃지 */}
                {expanded && showBadge && (
                  <Box
                    component="span"
                    aria-label={`미읽 알림 ${notifUnreadCount}개`}
                    sx={{
                      bgcolor: BRAND,
                      color: PAPER0,
                      fontSize: 9,
                      fontWeight: 700,
                      borderRadius: '9px',
                      px: '6px',
                      py: '1px',
                      fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                      lineHeight: 1.5,
                    }}
                  >
                    {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                  </Box>
                )}
              </ButtonBase>
            );
          })}
        </Box>

        {/* 하단 사용자 — SID-FE-01: 실데이터/스켈레톤/미표시 3분기 */}
        {/* [목적] authStatus 분기로 flash of wrong state 방지 */}
        {/* [이유] D-SID-SIDEBAR-LOADING: loading=스켈레톤, guest=미표시, authenticated=실데이터 */}
        {/* [사이드 이팩트] mock/fallback 렌더 제거 — 가짜 사용자 UI 노출 차단 */}
        {/* [수정 시 고려사항] user 필드(displayName/handle) 변경 시 MeOutput 타입과 동기 */}
        {authStatus === 'loading' && (
          <Stack
            direction="row"
            alignItems="center"
            spacing="9px"
            sx={{
              p: collapsed && !mobile ? '10px' : '10px 12px',
              borderTop: `1px solid ${BORDER_SOFT}`,
              justifyContent: collapsed && !mobile ? 'center' : 'flex-start',
            }}
          >
            <Skeleton variant="circular" width={30} height={30} sx={{ flexShrink: 0 }} />
            {expanded && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton width={80} height={14} />
                <Skeleton width={100} height={11} sx={{ mt: '2px' }} />
              </Box>
            )}
          </Stack>
        )}
        {/* LGO-FE-01: 하단 사용자 영역 — 데스크톱에서 클릭 가능한 팝오버 트리거 (B안, design-check 확정) */}
        {/* [목적] 사용자 영역 전체를 ButtonBase 트리거로 감싸 설정/로그아웃 팝오버 메뉴 열기 */}
        {/* [이유] shell.jsx:122 ⚙ 버튼 누락 복원 — 일관된 위치에서 설정/로그아웃 도달 가능 */}
        {/* [사이드 이팩트] 모바일(mobile=true) 에서는 클릭 이벤트 무시 — ProfileMobile 경로 유지 */}
        {/* [수정 시 고려사항] menuAnchorEl 기반 MUI Menu 는 collapsed/expanded 양쪽 동작. */}
        {/*                   아이콘·카피 "설정"/"로그아웃" "↩" 은 SettingsPage Row 와 통일(시안 정본). */}
        {authStatus === 'authenticated' && authUser !== null && (
          <>
            <ButtonBase
              id={userMenuTriggerId}
              component={Stack}
              direction="row"
              alignItems="center"
              spacing="9px"
              onClick={handleUserAreaClick}
              aria-haspopup="menu"
              aria-expanded={menuOpen ? 'true' : 'false'}
              aria-controls={menuOpen ? userMenuId : undefined}
              aria-label="사용자 메뉴"
              sx={{
                p: collapsed && !mobile ? '10px' : '10px 12px',
                borderTop: `1px solid ${BORDER_SOFT}`,
                justifyContent: collapsed && !mobile ? 'center' : 'flex-start',
                width: '100%',
                textAlign: 'left',
                cursor: mobile ? 'default' : 'pointer',
                '&:hover': {
                  bgcolor: mobile ? 'transparent' : 'rgba(0,0,0,.03)',
                },
                '&:focus-visible': {
                  outline: mobile ? 'none' : `2px solid ${BRAND}`,
                  outlineOffset: -2,
                },
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  // [이유] 사용자 아바타 그라디언트 — 시안 shell.jsx 원본값 유지(토큰에 brand 단계 없음).
                  //         check-no-cool-hex FORBIDDEN_HEX 대상 아님. (Sidebar.tsx:569 기존값 재사용)
                  background: 'linear-gradient(135deg,#2876D8,#1A4D8F)',
                  color: PAPER0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {authUser.displayName.charAt(0)}
              </Box>
              {expanded && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: INK_HI,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {authUser.displayName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 9.5,
                      color: INK_LOW,
                      fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    @{authUser.handle}
                  </Typography>
                </Box>
              )}
            </ButtonBase>

            {/* LGO-FE-01: 사용자 메뉴 팝오버 — 설정 + 로그아웃 */}
            {/* [접근성] role="menu" / role="menuitem" / Esc 닫기(MUI Menu 기본) / aria-labelledby */}
            {/* [토큰] shadow.modal / radius.m 사용. 색·간격은 tokens 참조 */}
            {!mobile && (
              <Menu
                id={userMenuId}
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': userMenuTriggerId,
                  role: 'menu',
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      boxShadow: tokens.shadow.modal,
                      borderRadius: `${tokens.radius.m}px`,
                      border: `1px solid ${BORDER_SOFT}`,
                      minWidth: 160,
                      overflow: 'visible',
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={handleMenuSettings}
                  role="menuitem"
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: INK_HI,
                    gap: '10px',
                    px: '14px',
                    py: '10px',
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: -2 },
                  }}
                >
                  <Box component="span" sx={{ display: 'flex' }}>
                    <LineIcon name="settings" size={14} />
                  </Box>
                  설정
                </MenuItem>
                <MenuItem
                  onClick={handleMenuLogout}
                  role="menuitem"
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: INK_HI,
                    gap: '10px',
                    px: '14px',
                    py: '10px',
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: -2 },
                  }}
                >
                  <Box component="span" aria-hidden="true" sx={{ fontSize: 14 }}>
                    ↩
                  </Box>
                  로그아웃
                </MenuItem>
              </Menu>
            )}
          </>
        )}
        {/* authStatus === 'guest': 인증 사용자 UI 미표시 (폴백 렌더 금지) */}

        {/* collapse 토글 버튼 — 데스크톱 전용 (W3-1). 시안 shell.jsx 원본대로 경계 걸침(right:-11).
            aside 에 overflow 미지정이라 잘리지 않고 온전한 원으로 표시됨. */}
        {!mobile && (
          <ButtonBase
            onClick={onToggle}
            aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
            aria-expanded={!collapsed}
            sx={{
              position: 'absolute',
              top: 18,
              right: -11,
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: PAPER0,
              border: `1px solid ${BORDER}`,
              color: INK_MID,
              cursor: 'pointer',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: tokens.shadow.card,
              zIndex: 2,
              '&:focus-visible': {
                outline: `2px solid ${BRAND}`,
                outlineOffset: 2,
              },
            }}
          >
            {collapsed ? '›' : '‹'}
          </ButtonBase>
        )}
      </Box>

      {/* G1-4: 커맨드 팔레트(⌘K) + 새 컬렉션 모달 — 데스크톱 전용(mobile 에서는 Sidebar 자체가 미마운트) */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <NewCollectionModal
        open={newCollectionOpen}
        onClose={() => setNewCollectionOpen(false)}
        onAuthRequired={() => setLoginPromptOpen(true)}
      />
      <LoginPromptModal
        open={loginPromptOpen}
        intent="collection.create"
        onClose={() => setLoginPromptOpen(false)}
      />
    </>
  );
}
